import { useSeo, useJsonLd } from "@/lib/seo";

// FAQs shown for every couture product (bridal / madetoorder friendly)
const DEFAULT_FAQS = [
  {
    q: "How long does a Barique couture piece take to be crafted?",
    a: "Every Barique piece is made to order in our New Delhi atelier and takes between 6 to 12 weeks depending on the level of embroidery and finishings involved. Bridal orders are recommended to be placed 3 to 4 months in advance.",
  },
  {
    q: "Do you ship worldwide?",
    a: "Yes. We ship couture pieces complimentary to every country. International orders are dispatched with signature-required, fully insured couriers.",
  },
  {
    q: "Can I request a custom size or a bespoke silhouette?",
    a: "Absolutely. Every piece can be tailored to your measurements. For bespoke silhouettes or private commissions, please book a couture consultation at our New Delhi atelier or over WhatsApp.",
  },
  {
    q: "What is the return and exchange policy?",
    a: "Because every couture piece is crafted to order, exchanges are offered only for manufacturing defects. Please write to us within 48 hours of delivery for assistance.",
  },
  {
    q: "How should the piece be cared for?",
    a: "All Barique couture must be dry-cleaned by a specialist in hand-embroidered textiles. Store flat, wrapped in muslin, away from direct sunlight and humidity.",
  },
];

export default function ProductSEO({ product }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const url = product ? `${origin}/product/${product.slug}` : origin;
  const images = ((product && product.images) || []).filter(Boolean);
  const primaryImage = images[0] || (product && product.image) || "";
  const description = product
    ? (product.description || product.subtitle || `${product.title} — hand-crafted couture from BARIQUE, New Delhi.`)
    : "";

  const priceINR = Number((product && product.price) || 0);

  // JSON-LD Product
  const productLd = product && {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.title,
    description,
    sku: product.id,
    ...(product.slug ? { mpn: product.slug } : {}),
    image: images.length ? images : (primaryImage ? [primaryImage] : undefined),
    brand: { "@type": "Brand", name: "BARIQUE" },
    category: product.category || "Couture",
    ...(product.colors?.length
      ? { color: product.colors.map((c) => (typeof c === "string" ? c : c.name)).filter(Boolean).join(", ") }
      : {}),
    ...(product.sizes?.length ? { size: product.sizes.join(", ") } : {}),
    offers: {
      "@type": "Offer",
      priceCurrency: "INR",
      price: priceINR.toFixed(2),
      url,
      availability: product.is_active === false ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
      itemCondition: "https://schema.org/NewCondition",
      seller: { "@type": "Organization", name: "BARIQUE" },
    },
  };

  // JSON-LD BreadcrumbList
  const breadcrumbLd = product && {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
      { "@type": "ListItem", position: 2, name: "Collections", item: `${origin}/collections` },
      ...(product.category
        ? [{
            "@type": "ListItem", position: 3,
            name: product.category.charAt(0).toUpperCase() + product.category.slice(1),
            item: `${origin}/collections/${product.category}`,
          }]
        : []),
      {
        "@type": "ListItem",
        position: product.category ? 4 : 3,
        name: product.title,
        item: url,
      },
    ],
  };

  // JSON-LD FAQPage
  const faqLd = product && {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: DEFAULT_FAQS.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };

  useSeo({
    title: product ? `${product.title} · BARIQUE` : "BARIQUE — Contemporary Indian Couture",
    description: description.slice(0, 160),
    canonical: url,
    image: primaryImage,
    type: product ? "product" : "website",
  });

  useJsonLd(product ? [productLd, breadcrumbLd, faqLd] : []);

  return null;
}
